/**
 * Finance Service
 * Handles financial data fetching
 * Mock/Real switching is handled at Service layer
 * Real API: GET /admin/finance/transactions, GET /admin/finance/transactions/{id}, GET /admin/finance/summary
 */

import { IS_MOCK, MOCK_DELAY } from '../../constants/config';
import { apiService } from '../api/ApiService';
import {
  mockFinancialTransactions,
  calculateFinancialSummary,
} from '../mock/mockFinance';
import { delay } from '../mock/mockData';
import type {
  FinancialTransaction,
  FinancialTransactionDetail,
  FinancialSummary,
  FinancialFilter,
  PaginatedFinancialTransactions,
  PaymentStatus,
  TransactionType,
} from '../../types/finance';
import type { MissionType } from '../../types/mission';
import { formatDateTime, parseDate } from '../../utils/dateUtils';

/** Backend PaymentTransactionManagementSummary (camelCase from API) */
interface ApiPaymentTransactionSummary {
  id: string;
  missionId: string;
  missionCode: string;
  missionTitle: string;
  missionType: MissionType;
  assetId: string;
  assetCallSign: string | null;
  assetEmail: string | null;
  ghostId: string | null;
  ghostCallSign: string | null;
  ghostEmail: string | null;
  amount: number;
  platformFee: number;
  bookingFee: number;
  ghostEarnings: number;
  currency: string;
  status: string; // PaymentStatus
  operationType: string; // PaymentOperationType
  providerReference: string | null;
  transactionAt: string | null;
}

interface ApiPaymentTransactionDetail extends ApiPaymentTransactionSummary {
  provider: string;
  paymentMethodReference: string | null;
  authorizationExpiresAt: string | null;
  settlementAmount: number | null;
  settledAt: string | null;
  voidedAt: string | null;
  failureReason: string | null;
  merchant: string;
  merchantAccount: string;
}

/** Backend PagedResponse<PaymentTransactionManagementSummary> */
interface ApiPagedTransactionsResponse {
  total: number;
  items: ApiPaymentTransactionSummary[];
}

/** Backend FinancialPeriodSummary */
interface ApiFinancialPeriodSummary {
  revenue: number;
  bookingFees: number;
  platformFees: number;
  ghostPayouts: number;
}

/** Backend FinancialSummary (camelCase from API) */
interface ApiFinancialSummaryResponse {
  totalRevenue: number;
  totalBookingFees: number;
  totalPlatformFees: number;
  totalGhostPayouts: number;
  netIncome: number;
  today: ApiFinancialPeriodSummary;
  thisMonth: ApiFinancialPeriodSummary;
  thisYear: ApiFinancialPeriodSummary;
}

/** Map backend PaymentOperationType to frontend TransactionType */
function mapApiOperationTypeToType(operationType: string): TransactionType {
  const m: Record<string, TransactionType> = {
    Settle: 'asset_payment',
    Refund: 'refund',
    Auth: 'asset_payment',
    Void: 'refund',
  };
  return m[operationType] ?? 'asset_payment';
}

/** Pick value from api with camelCase or PascalCase key */
function pick<T>(api: Record<string, unknown>, key: string): T | undefined {
  const lower = key.charAt(0).toLowerCase() + key.slice(1);
  const upper = key.charAt(0).toUpperCase() + key.slice(1);
  return ((api[lower] ?? api[upper]) as T) ?? undefined;
}

/** Map API transaction to FinancialTransaction (supports camelCase or PascalCase API) */
function mapApiTransactionToTransaction(api: ApiPaymentTransactionSummary | Record<string, unknown>): FinancialTransaction {
  const raw = api as Record<string, unknown>;
  const transactionAt = pick<string>(raw, 'transactionAt') ?? pick<string>(raw, 'TransactionAt');
  const date = parseDate(transactionAt) ?? new Date();
  const id = String(pick(raw, 'id') ?? pick(raw, 'Id') ?? '');
  const providerReference = pick<string>(raw, 'providerReference') ?? pick<string>(raw, 'ProviderReference');
  const apiMissionId = pick<string>(raw, 'missionId') ?? pick<string>(raw, 'MissionId');
  const missionCode = String(pick(raw, 'missionCode') ?? pick(raw, 'MissionCode') ?? '');
  const missionTitle = pick<string>(raw, 'missionTitle') ?? pick<string>(raw, 'MissionTitle');
  const missionType = (pick<string>(raw, 'missionType') ?? pick(raw, 'MissionType')) as MissionType | undefined;
  const operationType = String(pick(raw, 'operationType') ?? pick(raw, 'OperationType') ?? '');
  const status = String(pick(raw, 'status') ?? pick(raw, 'Status') ?? '') as PaymentStatus;
  const amount = Number(pick(raw, 'amount') ?? pick(raw, 'Amount') ?? 0);
  const bookingFee = Number(pick(raw, 'bookingFee') ?? pick(raw, 'BookingFee') ?? 0);
  const platformFee = Number(pick(raw, 'platformFee') ?? pick(raw, 'PlatformFee') ?? 0);
  const ghostEarnings = Number(pick(raw, 'ghostEarnings') ?? pick(raw, 'GhostEarnings') ?? 0);
  const assetId = String(pick(raw, 'assetId') ?? pick(raw, 'AssetId') ?? '');
  const assetCallSign = pick<string>(raw, 'assetCallSign') ?? pick<string>(raw, 'AssetCallSign');
  const assetEmail = pick<string>(raw, 'assetEmail') ?? pick<string>(raw, 'AssetEmail');
  const ghostId = pick<string>(raw, 'ghostId') ?? pick<string>(raw, 'GhostId');
  const ghostCallSign = pick<string>(raw, 'ghostCallSign') ?? pick<string>(raw, 'GhostCallSign');
  const ghostEmail = pick<string>(raw, 'ghostEmail') ?? pick<string>(raw, 'GhostEmail');
  return {
    id,
    transactionId: providerReference ?? id,
    missionId: apiMissionId ?? undefined,
    missionCode: missionCode || undefined,
    missionTitle: missionTitle ?? undefined,
    missionType: missionType ?? 'QuickHit',
    type: mapApiOperationTypeToType(operationType),
    status,
    amount,
    assetBookingFee: bookingFee,
    platformFee,
    ghostEarnings,
    assetId,
    assetName: assetCallSign ?? undefined,
    assetEmail: assetEmail ?? undefined,
    ghostId: ghostId ?? undefined,
    ghostName: ghostCallSign ?? undefined,
    ghostEmail: ghostEmail ?? undefined,
    date,
    dateText: formatDateTime(date),
  };
}

function mapApiTransactionToDetail(api: ApiPaymentTransactionDetail | Record<string, unknown>): FinancialTransactionDetail {
  const raw = api as Record<string, unknown>;
  const base = mapApiTransactionToTransaction(raw);
  const providerReference = pick<string>(raw, 'providerReference') ?? pick<string>(raw, 'ProviderReference');
  const authorizationExpiresAt = parseDate(
    pick<string>(raw, 'authorizationExpiresAt') ?? pick<string>(raw, 'AuthorizationExpiresAt')
  );
  const settledAt = parseDate(pick<string>(raw, 'settledAt') ?? pick<string>(raw, 'SettledAt'));
  const voidedAt = parseDate(pick<string>(raw, 'voidedAt') ?? pick<string>(raw, 'VoidedAt'));

  return {
    ...base,
    provider: pick<string>(raw, 'provider') ?? pick<string>(raw, 'Provider') ?? undefined,
    operationType: String(pick(raw, 'operationType') ?? pick(raw, 'OperationType') ?? ''),
    paymentMethodReference: pick<string>(raw, 'paymentMethodReference') ?? pick<string>(raw, 'PaymentMethodReference') ?? undefined,
    providerReference: providerReference ?? undefined,
    authorizationExpiresAt: authorizationExpiresAt ?? undefined,
    settlementAmount: Number(pick(raw, 'settlementAmount') ?? pick(raw, 'SettlementAmount') ?? 0),
    settledAt: settledAt ?? undefined,
    voidedAt: voidedAt ?? undefined,
    failureReason: pick<string>(raw, 'failureReason') ?? pick<string>(raw, 'FailureReason') ?? undefined,
    merchant: pick<string>(raw, 'merchant') ?? pick<string>(raw, 'Merchant') ?? undefined,
    merchantAccount: pick<string>(raw, 'merchantAccount') ?? pick<string>(raw, 'MerchantAccount') ?? undefined,
  };
}

/** Map API summary to FinancialSummary (supports camelCase or PascalCase API) */
function mapApiSummaryToSummary(api: ApiFinancialSummaryResponse | Record<string, unknown>): FinancialSummary {
  const r = api as Record<string, unknown>;
  const num = (key: string) => Number(
    r[key] ?? r[key.charAt(0).toUpperCase() + key.slice(1)] ?? 0
  );
  const period = (key: string) => {
    const p = (r[key] ?? r[key.charAt(0).toUpperCase() + key.slice(1)]) as Record<string, unknown> | undefined;
    if (!p) return { revenue: 0, bookingFees: 0, platformFees: 0, ghostPayouts: 0 };
    const n = (k: string) => Number(p[k] ?? (p as Record<string, unknown>)[k.charAt(0).toUpperCase() + k.slice(1)] ?? 0);
    return { revenue: n('revenue'), bookingFees: n('bookingFees'), platformFees: n('platformFees'), ghostPayouts: n('ghostPayouts') };
  };
  return {
    totalRevenue: num('totalRevenue'),
    totalBookingFees: num('totalBookingFees'),
    totalPlatformFees: num('totalPlatformFees'),
    totalGhostPayouts: num('totalGhostPayouts'),
    netIncome: num('netIncome'),
    today: period('today'),
    thisMonth: period('thisMonth'),
    thisYear: period('thisYear'),
  };
}

class FinanceService {
  /**
   * Get paginated financial transactions with optional filters
   */
  static async getTransactions(
    page: number = 1,
    pageSize: number = 20,
    filter?: FinancialFilter
  ): Promise<PaginatedFinancialTransactions> {
    if (IS_MOCK) {
      await delay(MOCK_DELAY);

      let filteredTransactions = [...mockFinancialTransactions];

      // Apply status filter
      if (filter?.status && filter.status !== 'all') {
        const statuses = Array.isArray(filter.status) ? filter.status : [filter.status];
        filteredTransactions = filteredTransactions.filter(t => statuses.includes(t.status));
      }

      // Apply date range filter
      if (filter?.dateRange) {
        filteredTransactions = filteredTransactions.filter(
          t => t.date >= filter.dateRange!.start && t.date <= filter.dateRange!.end
        );
      }

      // Apply search filter
      if (filter?.search) {
        const searchLower = filter.search.toLowerCase();
        filteredTransactions = filteredTransactions.filter(
          t =>
            t.transactionId.toLowerCase().includes(searchLower) ||
            t.missionId?.toLowerCase().includes(searchLower) ||
            t.assetName?.toLowerCase().includes(searchLower) ||
            t.ghostName?.toLowerCase().includes(searchLower) ||
            t.description?.toLowerCase().includes(searchLower)
        );
      }

      const total = filteredTransactions.length;
      const totalPages = Math.ceil(total / pageSize);
      const startIndex = (page - 1) * pageSize;
      const data = filteredTransactions.slice(startIndex, startIndex + pageSize);

      return {
        data,
        total,
        page,
        pageSize,
        totalPages,
      };
    } else {
      // Backend: GetPaymentTransactionsQuery (camelCase query params)
      const params = new URLSearchParams({
        page: page.toString(),
        pageSize: pageSize.toString(),
      });
      if (filter?.missionId) params.append('search', filter.missionId);
      if (filter?.search) params.append('search', filter.search);
      if (filter?.dateRange) {
        params.append('startDate', filter.dateRange.start.toISOString());
        params.append('endDate', filter.dateRange.end.toISOString());
      }
      // Status: send each selected status as a separate query param
      if (filter?.status && filter.status !== 'all') {
        const statuses = Array.isArray(filter.status) ? filter.status : [filter.status];
        statuses.forEach(s => params.append('status', s));
      }

      const response = await apiService.get<{
        isSuccess: boolean;
        data: ApiPagedTransactionsResponse;
        message?: string;
      }>(`/admin/finance/transactions?${params.toString()}`);

      const raw = response as any;
      const payload = raw.data ?? raw.Data;
      if (!raw.isSuccess && raw.IsSuccess !== true) {
        throw new Error(raw.message ?? 'Failed to fetch financial transactions');
      }
      if (!payload) {
        throw new Error('Invalid response: missing transactions data');
      }
      const items = payload.items ?? payload.Items;
      const total = payload.total ?? payload.Total ?? 0;
      const data = (Array.isArray(items) ? items : []).map(mapApiTransactionToTransaction);
      const totalPages = Math.ceil(total / pageSize);
      return { data, total, page, pageSize, totalPages };
    }
  }

  /**
   * Get financial summary
   */
  static async getFinancialSummary(
    filter?: FinancialFilter
  ): Promise<FinancialSummary> {
    if (IS_MOCK) {
      await delay(MOCK_DELAY);
      return calculateFinancialSummary(mockFinancialTransactions, filter);
    } else {
      // Backend: GetFinancialSummaryQuery (startDate, endDate only)
      const params = new URLSearchParams();
      if (filter?.dateRange) {
        params.append('startDate', filter.dateRange.start.toISOString());
        params.append('endDate', filter.dateRange.end.toISOString());
      }

      const response = await apiService.get<{
        isSuccess: boolean;
        data: ApiFinancialSummaryResponse;
        message?: string;
      }>(`/admin/finance/summary?${params.toString()}`);

      const raw = response as { isSuccess?: boolean; data?: ApiFinancialSummaryResponse; Data?: ApiFinancialSummaryResponse; message?: string };
      const payload = raw.data ?? raw.Data;
      if (!raw.isSuccess && (raw as { IsSuccess?: boolean }).IsSuccess !== true) {
        throw new Error((raw as { message?: string }).message ?? 'Failed to fetch financial summary');
      }
      if (!payload) {
        throw new Error('Invalid response: missing summary data');
      }
      return mapApiSummaryToSummary(payload);
    }
  }

  /**
   * Get transaction detail by ID
   */
  static async getTransactionDetail(id: string): Promise<FinancialTransactionDetail> {
    if (IS_MOCK) {
      await delay(MOCK_DELAY);
      const transaction = mockFinancialTransactions.find(t => t.id === id || t.transactionId === id);
      if (!transaction) {
        throw new Error('Transaction not found');
      }

      const isRefund = transaction.type === 'refund';
      return {
        ...transaction,
        merchant: 'Testing',
        merchantAccount: 'testing',
        provider: 'Stripe',
        operationType: isRefund ? 'Refund' : 'Auth',
        paymentMethodReference: 'pm_mock_finance_detail',
        providerReference: transaction.transactionId,
        authorizationExpiresAt: isRefund
          ? undefined
          : new Date(transaction.date.getTime() + 7 * 24 * 60 * 60 * 1000),
        settlementAmount: isRefund ? 0 : transaction.amount,
        settledAt: transaction.status === 'Settled' ? transaction.date : undefined,
        voidedAt: transaction.status === 'Voided' ? transaction.date : undefined,
        failureReason: transaction.status === 'Failed' ? 'Mock payment processing failure.' : undefined,
      };
    } else {
      const response = await apiService.get<{
        isSuccess: boolean;
        data: ApiPaymentTransactionDetail;
        message?: string;
      }>(`/admin/finance/transactions/${id}`);

      const raw = response as any;
      const payload = raw.data ?? raw.Data;
      if (!raw.isSuccess && raw.IsSuccess !== true) {
        throw new Error(raw.message ?? 'Failed to fetch transaction detail');
      }
      if (!payload) {
        throw new Error('Invalid response: missing transaction detail');
      }
      return mapApiTransactionToDetail(payload);
    }
  }
}

export { FinanceService };
